import { memo, ReactNode } from 'react'
import {
  FieldPath,
  FieldValues,
  useController,
  UseControllerProps,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  Select as MuiSelect,
  SelectProps,
} from '@mui/material'

interface SelectComponentProps<
  T extends FieldValues,
  S extends FieldPath<T>,
> extends UseControllerProps<T, S> {
  label?: string
  children: ReactNode
  clearable?: boolean
  selectProps?: Omit<SelectProps, 'value' | 'onChange' | 'error'>
}

const Select = <
  T extends FieldValues = FieldValues,
  S extends FieldPath<T> = FieldPath<T>,
>({
  name,
  label,
  children,
  clearable = false,
  selectProps,
  ...controllerProps
}: SelectComponentProps<T, S>) => {
  const { t } = useTranslation()
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({ name, ...controllerProps })

  const handleClear = () => {
    onChange(null)
  }

  return (
    <FormControl fullWidth error={!!error}>
      {label && <InputLabel>{label}</InputLabel>}
      <MuiSelect
        value={value ?? ''}
        onChange={onChange}
        label={label}
        endAdornment={
          clearable && value ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                edge="end"
                sx={{ mr: 1 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
        {...selectProps}
      >
        {children}
      </MuiSelect>
      {error && (
        <FormHelperText>
          {error?.type === 'custom'
            ? t(`error.${error.type}.${error.message}`)
            : error?.message}
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default memo(Select) as typeof Select
